/**
 * Anchor Navigation Handler
 * Provides smooth scrolling to sections with hash anchors, accounting for sticky header offset
 */

class AnchorNavigation {
  constructor() {
    this.headerOffset = 0;
    this.scrollTimeout = null;
    this.init();
  }

  /**
   * Initialize the anchor navigation system
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Setup event listeners and handle initial hash
   */
  setup() {
    this.calculateHeaderOffset();
    
    // Listen for hash link clicks
    document.addEventListener('click', this.handleClick.bind(this));
    
    // Listen for hash changes (browser back/forward)
    window.addEventListener('hashchange', this.handleHashChange.bind(this));
    
    // Handle initial hash if present
    this.handleInitialHash();
    
    // Recalculate header offset on resize
    window.addEventListener('resize', this.debounce(() => {
      this.calculateHeaderOffset();
    }, 100));
  }

  /**
   * Calculate the current header offset with enhanced form factor support
   */
  calculateHeaderOffset() {
    // Get the header element
    const header = document.querySelector('#header-component');
    const headerGroup = document.querySelector('#header-group');
    
    let offset = 0;
    
    if (header) {
      // Use the actual header height
      offset = header.offsetHeight;
    } else if (headerGroup) {
      // Fallback to header group if main header not found
      offset = headerGroup.offsetHeight;
    } else {
      // Final fallback to CSS custom property
      const headerHeight = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height');
      offset = headerHeight ? parseInt(headerHeight) : 0;
    }
    
    // Add extra padding for better visual spacing on all form factors
    const extraPadding = this.getExtraPadding();
    this.headerOffset = offset + extraPadding;
  }

  /**
   * Get extra padding based on form factor for better visual spacing
   */
  getExtraPadding() {
    const viewportWidth = window.innerWidth;
    
    // Mobile: smaller padding
    if (viewportWidth < 750) {
      return 16;
    }
    // Tablet: medium padding  
    else if (viewportWidth < 1024) {
      return 20;
    }
    // Desktop: standard padding
    else {
      return 24;
    }
  }

  /**
   * Handle clicks on hash links
   */
  handleClick(event) {
    const anchor = event.target.closest('a[href]');
    if (!anchor) return;

    let url;
    try {
      url = new URL(anchor.getAttribute('href'), window.location.href);
    } catch (_) {
      return;
    }

    const { origin, pathname, hash } = url;
    if (!hash || hash === '#') return;

    // Intercept in-page hash links (same origin + pathname) so we don't add history entries
    if (origin === window.location.origin && pathname === window.location.pathname) {
      event.preventDefault();
      this.scrollToSection(hash);
    }
  }

  /**
   * Handle hash changes from browser navigation
   */
  handleHashChange(event) {
    const hash = window.location.hash;
    if (hash) {
      this.scrollToSection(hash);
    }
  }

  /**
   * Handle initial hash on page load
   */
  handleInitialHash() {
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        this.scrollToSection(hash);
      }, 100);
    }
  }

  /**
   * Scroll to a section with smooth scrolling and header offset
   */
  scrollToSection(hash) {
    const targetId = hash.substring(1); // Remove the # symbol
    if (!targetId) return;

    const targetElement = document.getElementById(targetId);
    if (!targetElement) {
      console.warn(`Anchor navigation: Element with ID "${targetId}" not found`);
      return;
    }

    // Clear any existing scroll timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Recalculate header offset to ensure accuracy
    this.calculateHeaderOffset();

    // Prefer precise positioning using bounding rect to account for layout shifts
    const rect = targetElement.getBoundingClientRect();
    const absoluteTop = window.scrollY + rect.top;
    const targetPosition = Math.max(0, absoluteTop - this.headerOffset);

    // Smooth scroll to the target position
    window.scrollTo({ top: targetPosition, behavior: 'smooth' });

    // Verify scroll position after a delay and adjust if needed
    this.scrollTimeout = setTimeout(() => {
      this.verifyScrollPosition(targetElement, hash);
    }, 180);
  }

  /**
   * Verify and adjust scroll position if needed
   */
  verifyScrollPosition(targetElement, hash) {
    const rect = targetElement.getBoundingClientRect();
    const desiredTop = Math.max(0, window.scrollY + rect.top - this.headerOffset);
    const delta = desiredTop - window.scrollY;

    // If we're off by more than 2px, correct without animation to avoid bounce
    if (Math.abs(delta) > 2) {
      window.scrollTo({ top: desiredTop, behavior: 'auto' });
    }
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize anchor navigation when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AnchorNavigation();
  });
} else {
  new AnchorNavigation();
}
